import { useEffect, useRef, useState } from "react";
import { api, formatNumber } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

export default function AdminPage() {
  const { user, loading, requireAuth } = useAuth();
  const [section, setSection] = useState("messages"); // messages | reports | appeals
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("under_review");
  const [reportStatus, setReportStatus] = useState("open");
  const [appealStatus, setAppealStatus] = useState("open");
  const askedAuthRef = useRef(false);

  const load = async () => {
    try {
      if (section === "messages") {
        const r = await api.get(`/admin/messages${tab === "all" ? "" : `?status=${tab}`}`);
        setItems(r.data);
      } else if (section === "reports") {
        const r = await api.get(`/admin/reports?status=${reportStatus}`);
        setItems(r.data);
      } else if (section === "appeals") {
        const r = await api.get(`/admin/appeals?status=${appealStatus}`);
        setItems(r.data);
      }
    } catch (_) { setItems([]); }
  };

  useEffect(() => {
    if (loading) return;
    if (!user) {
      if (!askedAuthRef.current) { askedAuthRef.current = true; requireAuth(() => {}); }
      return;
    }
    if (!user.is_admin) return;  // don't fire /admin/* until we know the user is admin
    askedAuthRef.current = false;
    load();
    // eslint-disable-next-line
  }, [section, tab, reportStatus, appealStatus, user, loading]);

  const act = async (path, ok = "Done") => {
    try { await api.post(path); toast.success(ok); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  if (!user) return <div className="max-w-4xl mx-auto px-6 py-24 oh-label">Sign in required.</div>;
  if (!user.is_admin) return <div className="max-w-4xl mx-auto px-6 py-24 oh-label">Admin only.</div>;

  const SECTIONS = [
    { key: "messages", label: "Messages" },
    { key: "reports",  label: "Reports" },
    { key: "appeals",  label: "Appeals" },
  ];

  return (
    <div className="max-w-[1200px] mx-auto px-6 sm:px-10 pt-14 pb-24">
      <div className="oh-label">Admin</div>
      <h1 className="font-serif-editorial text-4xl sm:text-5xl mt-3">Guardrails for meaning.</h1>

      <div className="mt-8 flex gap-6 border-b oh-hairline">
        {SECTIONS.map((s) => (
          <button key={s.key} onClick={() => setSection(s.key)}
                  className={`py-3 text-xs uppercase tracking-wider transition-colors ${section === s.key ? "border-b-2 border-[color:var(--oh-ink)]" : "text-[color:var(--oh-ink-2)]"}`}
                  data-testid={`admin-section-${s.key}`}>
            {s.label}
          </button>
        ))}
      </div>

      {section === "messages" && (
        <>
          <div className="mt-6 flex gap-1 flex-wrap border-b oh-hairline">
            {["under_review", "TRAVELLING", "HEARD", "hidden", "rejected", "all"].map((k) => (
              <button key={k} onClick={() => setTab(k)}
                      className={`px-4 py-3 text-xs uppercase tracking-wider ${tab === k ? "border-b-2 border-[color:var(--oh-ink)]" : "text-[color:var(--oh-ink-2)]"}`}
                      data-testid={`admin-tab-${k}`}>
                {k.replace("_", " ")}
              </button>
            ))}
          </div>
          <div className="mt-8 divide-y oh-hairline">
            {items.length === 0 && <div className="py-16 oh-label">Nothing here.</div>}
            {items.map((m) => (
              <div key={m.id} className="py-6 grid grid-cols-12 gap-4 items-start" data-testid={`admin-row-${m.id}`}>
                <div className="col-span-12 sm:col-span-6">
                  <div className="oh-label">{m.destination_label || m.destination_type} · {m.origin_country}</div>
                  <div className="font-serif-editorial text-2xl mt-2">{m.title}</div>
                  <div className="text-sm text-[color:var(--oh-ink-2)] mt-1">"{m.text}"</div>
                </div>
                <div className="col-span-6 sm:col-span-3 oh-label">
                  {formatNumber(m.voices_count)} voices · {m.status} · {m.reports_count || 0} reports
                </div>
                <div className="col-span-6 sm:col-span-3 flex gap-2 justify-end flex-wrap">
                  {m.status === "under_review" && (
                    <button onClick={() => act(`/admin/messages/${m.id}/approve`, "Approved")}
                            className="admin-btn" data-testid={`admin-approve-${m.id}`}>Approve</button>
                  )}
                  {m.status !== "HEARD" && m.status !== "hidden" && (
                    <button onClick={() => act(`/admin/messages/${m.id}/heard`, "Marked heard")}
                            className="admin-btn" data-testid={`admin-heard-${m.id}`}>Mark heard</button>
                  )}
                  {m.status === "TRAVELLING" && (
                    <button onClick={() => act(`/admin/messages/${m.id}/hide`, "Hidden")}
                            className="admin-btn-red" data-testid={`admin-hide-${m.id}`}>Hide</button>
                  )}
                  {m.status === "hidden" && (
                    <button onClick={() => act(`/admin/messages/${m.id}/restore`, "Restored")}
                            className="admin-btn" data-testid={`admin-restore-${m.id}`}>Restore</button>
                  )}
                  <button onClick={() => act(`/admin/messages/${m.id}/moderate`, "Re-moderated")}
                          className="admin-btn" data-testid={`admin-remoderate-${m.id}`}>Re-moderate</button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {section === "reports" && (
        <>
          <div className="mt-6 flex gap-1 flex-wrap border-b oh-hairline">
            {["open", "dismissed", "all"].map((k) => (
              <button key={k} onClick={() => setReportStatus(k)}
                      className={`px-4 py-3 text-xs uppercase tracking-wider ${reportStatus === k ? "border-b-2 border-[color:var(--oh-ink)]" : "text-[color:var(--oh-ink-2)]"}`}
                      data-testid={`admin-reports-tab-${k}`}>{k}</button>
            ))}
          </div>
          <div className="mt-8 divide-y oh-hairline">
            {items.length === 0 && <div className="py-16 oh-label">Nothing here.</div>}
            {items.map((r) => (
              <div key={r.id} className="py-6 grid grid-cols-12 gap-4 items-start" data-testid={`admin-report-${r.id}`}>
                <div className="col-span-12 sm:col-span-6">
                  <div className="oh-label text-[color:var(--oh-red)]">{r.reason.toUpperCase()}</div>
                  <div className="font-serif-editorial text-2xl mt-2">{r.message?.title || r.message_id}</div>
                  {r.detail && <div className="text-sm text-[color:var(--oh-ink-2)] mt-1">"{r.detail}"</div>}
                  <div className="oh-label mt-2 text-[color:var(--oh-ink-3)]">
                    {new Date(r.created_at).toLocaleString()} · {r.message?.reports_count || 1} total reports on this MESSAGE · MESSAGE status {r.message?.status}
                  </div>
                </div>
                <div className="col-span-12 sm:col-span-6 flex gap-2 justify-end flex-wrap">
                  {r.status === "open" && (
                    <button onClick={() => act(`/admin/reports/${r.id}/dismiss`, "Dismissed")}
                            className="admin-btn" data-testid={`admin-report-dismiss-${r.id}`}>Dismiss</button>
                  )}
                  <button onClick={() => act(`/admin/messages/${r.message_id}/hide`, "Hidden")}
                          className="admin-btn-red" data-testid={`admin-report-hide-${r.id}`}>Hide MESSAGE</button>
                  <button onClick={() => act(`/admin/messages/${r.message_id}/restore`, "Restored")}
                          className="admin-btn" data-testid={`admin-report-restore-${r.id}`}>Restore</button>
                  <button onClick={() => act(`/admin/messages/${r.message_id}/moderate`, "Re-moderated")}
                          className="admin-btn" data-testid={`admin-report-remoderate-${r.id}`}>Re-moderate</button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {section === "appeals" && (
        <>
          <div className="mt-6 flex gap-1 flex-wrap border-b oh-hairline">
            {["open", "approved", "upheld", "all"].map((k) => (
              <button key={k} onClick={() => setAppealStatus(k)}
                      className={`px-4 py-3 text-xs uppercase tracking-wider ${appealStatus === k ? "border-b-2 border-[color:var(--oh-ink)]" : "text-[color:var(--oh-ink-2)]"}`}
                      data-testid={`admin-appeals-tab-${k}`}>{k}</button>
            ))}
          </div>
          <div className="mt-8 divide-y oh-hairline">
            {items.length === 0 && <div className="py-16 oh-label">Nothing here.</div>}
            {items.map((a) => (
              <div key={a.id} className="py-6 grid grid-cols-12 gap-4 items-start" data-testid={`admin-appeal-${a.id}`}>
                <div className="col-span-12 sm:col-span-8">
                  <div className="oh-label text-[color:var(--oh-red)]">APPEAL · {a.status.toUpperCase()}</div>
                  <div className="font-serif-editorial text-2xl mt-2">{a.rejected_title}</div>
                  <div className="text-sm text-[color:var(--oh-ink-2)] mt-1">"{a.rejected_text}"</div>
                  <div className="oh-label mt-3">Appeal reason</div>
                  <div className="text-sm text-[color:var(--oh-ink-2)] mt-1">{a.appeal_text}</div>
                </div>
                <div className="col-span-12 sm:col-span-4 flex gap-2 justify-end flex-wrap">
                  {a.status === "open" && (
                    <>
                      <button onClick={() => act(`/admin/appeals/${a.id}/approve`, "Approved")}
                              className="admin-btn" data-testid={`admin-appeal-approve-${a.id}`}>Approve</button>
                      <button onClick={() => act(`/admin/appeals/${a.id}/uphold`, "Upheld")}
                              className="admin-btn-red" data-testid={`admin-appeal-uphold-${a.id}`}>Uphold block</button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      <style>{`
        .admin-btn { font-size: .68rem; text-transform: uppercase; letter-spacing: .1em;
                     border: 1px solid var(--oh-ink-3); padding: .55rem .8rem; }
        .admin-btn:hover { background: var(--oh-bg-2); }
        .admin-btn-red { font-size: .68rem; text-transform: uppercase; letter-spacing: .1em;
                         border: 1px solid var(--oh-red); color: var(--oh-red);
                         padding: .55rem .8rem; }
        .admin-btn-red:hover { background: var(--oh-red); color: white; }
      `}</style>
    </div>
  );
}
